/**
 * useSubdomain
 *
 * Tells the rest of the public-jobs UI which company subdomain (if any)
 * the user is currently visiting.
 *
 * Resolution order:
 *   1. `?subdomain=acme` query param — wins over everything (handy for
 *      local dev and for testing without `/etc/hosts`).
 *   2. `window.location.hostname` — production path. We extract the
 *      first DNS label and treat it as the tenant slug, with two
 *      exclusions:
 *        • plain hosts (localhost, 127.0.0.1, raw IPs) → no subdomain
 *        • reserved labels (www, api, app, …)         → no subdomain
 *
 * Returning `null` means "render the master listing"; returning a slug
 * means "render the company-scoped landing page for that slug".
 */

const RESERVED_SUBDOMAINS = new Set([
  'www', 'api', 'app', 'admin', 'auth', 'login', 'logout',
  'mail', 'blog', 'docs', 'help', 'support', 'status',
  'cdn', 'static', 'assets', 'images', 'img', 'media',
  'jobs', 'careers', 'recruiter', 'employer', 'dashboard',
  'account', 'profile', 'settings', 'billing',
]);

function isPlainHost(hostname) {
  if (!hostname) return true;
  if (hostname === 'localhost') return true;
  // bare IPv4
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return true;
  return false;
}

export function getSubdomainFromLocation() {
  if (typeof window === 'undefined') return null;

  // Query-param override — useful in dev and for previewing tenants
  // without having to mess with DNS or hosts files.
  try {
    const sp = new URLSearchParams(window.location.search);
    const fromQuery = (sp.get('subdomain') || '').trim().toLowerCase();
    if (fromQuery && !RESERVED_SUBDOMAINS.has(fromQuery)) {
      return fromQuery;
    }
  } catch (_) {
    // ignore — URLSearchParams isn't available in some old runtimes
  }

  const hostname = window.location.hostname || '';
  if (isPlainHost(hostname)) return null;

  const parts = hostname.split('.');
  // yourapp.com (2 parts) or some.weird (still 2) → no subdomain
  if (parts.length < 3) return null;

  const candidate = parts[0].toLowerCase();
  if (!candidate) return null;
  if (RESERVED_SUBDOMAINS.has(candidate)) return null;

  return candidate;
}

/**
 * React-hook flavour, in case a component prefers the subscription form.
 * The value is stable for the lifetime of the page (subdomain doesn't
 * change without a navigation), so we return it from a single call.
 */
export function useSubdomain() {
  return getSubdomainFromLocation();
}

export default useSubdomain;
